import { useContext, useMemo, useState, type ReactNode } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getSearchApiBaseUrl, isStaging } from 'ui';
import { AuthContext } from './auth/Auth';
import { SiteNav } from './marketingNav/SiteNav';
import { useLoginState } from './studioxr/auth/LoginState';
import { trackEvent } from './libs/analyticsManager';

// /mcp-connect — the WorkOS AuthKit Standalone Connect Login URI for the
// Mattoboard MCP server. AuthKit redirects an MCP client's OAuth flow here
// with ?external_auth_id=<temp id>; the signed-in user confirms, we exchange
// their Firebase idToken at the backend's /mcp/connect/complete (which holds
// the WorkOS API key and the Pro gate), and follow the redirect_uri WorkOS
// returns. Non-Pro users get the upgrade state instead — issuance is
// Pro-only by design.
//
// Not a marketing URL, so signing in keeps the user on this page (no
// redirectTo stamp); SignUp's skipRedirectPaths includes /mcp-connect for
// the fresh-signup case.

/**
 * Backend origin for the complete call. Defaults to the same resolution the
 * library uses; `?mcpBackend=<suffix>` targets a preview Cloud Run service —
 * suffix-only and project-pinned, so the page can never be pointed at a
 * non-Mattoboard host (it forwards the user's idToken). A dev-only knob:
 * production hostnames ignore it entirely, so a crafted link can't route a
 * prod user's token to a preview service.
 */
export function mcpConnectBackendBase(search: string): string {
  const suffix = new URLSearchParams(search).get('mcpBackend') || '';
  if (isStaging() && /^[a-z0-9-]{1,30}$/.test(suffix)) {
    return `https://design-stream-backend-${suffix}-829457126264.us-central1.run.app`;
  }
  // The pre-launch Login URI is the mcp-connect hosting preview channel, and
  // WorkOS may not preserve query params on it — map that host to the MCP
  // preview service directly (the staging backend has no WORKOS_API_KEY yet).
  if (
    typeof window !== 'undefined' &&
    window.location?.hostname?.startsWith('mattoboard-staging--mcp-connect')
  ) {
    return 'https://design-stream-backend-mcp-829457126264.us-central1.run.app';
  }
  return getSearchApiBaseUrl();
}

type Phase = 'idle' | 'working' | 'declined' | 'error';

const UPGRADE_URL =
  'https://www.mattoboard.com/pricing?utm_source=mcp&utm_medium=connect&utm_campaign=mcp_pro_gate';

const CenterCard = ({ children }: { children: ReactNode }) => (
  <Stack sx={{ minHeight: { xs: '100svh', md: '100vh' } }}>
    <SiteNav />
    <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Stack
        sx={{
          maxWidth: 420,
          width: '100%',
          gap: 2,
          p: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.300',
          textAlign: 'center',
          alignItems: 'center',
        }}
      >
        {children}
      </Stack>
    </Stack>
  </Stack>
);

export const McpConnectPage = () => {
  const { currentUser } = useContext(AuthContext);
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const { search } = useLocation();
  const externalAuthId = useMemo(
    () => new URLSearchParams(search).get('external_auth_id') || '',
    [search],
  );
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorText, setErrorText] = useState('');

  const connect = async () => {
    if (!currentUser) return;
    setPhase('working');
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${mcpConnectBackendBase(search)}/mcp/connect/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, external_auth_id: externalAuthId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.redirect_uri) {
        trackEvent('MCP Connect - Completed');
        window.location.assign(data.redirect_uri);
        return;
      }
      if (res.ok && data.declined) {
        trackEvent('MCP Connect - Declined Free');
        setPhase('declined');
        return;
      }
      setErrorText(data.guidance || 'Something went wrong completing the connection.');
      setPhase('error');
    } catch {
      setErrorText('Could not reach Mattoboard. Check your connection and try again.');
      setPhase('error');
    }
  };

  if (!externalAuthId) {
    return (
      <CenterCard>
        <Typography variant="h6">Connection link expired</Typography>
        <Typography color="text.secondary">
          This page completes a connection started from an AI app. Start the
          connection again from your MCP client.
        </Typography>
      </CenterCard>
    );
  }

  if (!currentUser) {
    return (
      <CenterCard>
        <Typography variant="h6">Connect Mattoboard</Typography>
        <Typography color="text.secondary">
          Sign in to connect your Mattoboard account to your AI app.
        </Typography>
        <Button variant="contained" onClick={() => setShowLogin('signin')} sx={{ textTransform: 'none' }}>
          Sign in
        </Button>
        <Button onClick={() => setShowLogin('signup')} sx={{ textTransform: 'none' }}>
          Create an account
        </Button>
      </CenterCard>
    );
  }

  if (phase === 'declined') {
    return (
      <CenterCard>
        <Typography variant="h6">Mattoboard Pro required</Typography>
        <Typography color="text.secondary">
          Connecting Mattoboard to AI apps is a Pro feature. Upgrade to search
          the material library and open your concepts from your AI assistant.
        </Typography>
        <Button variant="contained" href={UPGRADE_URL} sx={{ textTransform: 'none' }}>
          See Pro plans
        </Button>
      </CenterCard>
    );
  }

  return (
    <CenterCard>
      <Typography variant="h6">Connect Mattoboard</Typography>
      <Typography color="text.secondary">
        Your AI app is asking to access Mattoboard as{' '}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {currentUser.email}
        </Box>
        . It will be able to search materials and read your concepts.
      </Typography>
      {phase === 'error' && (
        <Typography color="error" sx={{ fontSize: '0.9rem' }}>
          {errorText}
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={connect}
        disabled={phase === 'working'}
        startIcon={phase === 'working' ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{ textTransform: 'none' }}
      >
        {phase === 'working' ? 'Connecting…' : 'Connect'}
      </Button>
      {/* Anti-phishing line: the flow can be started by anyone and this link
          sent to a victim (external_auth_id is not session-bound), so the one
          defense is the user knowing the request should be their own. */}
      <Typography variant="caption" color="text.disabled">
        Only continue if you just started this connection from your own AI
        app.
      </Typography>
    </CenterCard>
  );
};
